import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  ProgressBar,
  Badge,
  Table,
  Modal,
  Form,
  Alert,
  Tab,
  Nav,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { installationAPI } from "../services/api";
const POS_COLOR = "#9370DB";

const CustomerDashboard = () => {
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isLogin, setIsLogin] = useState(true);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [requestForm, setRequestForm] = useState({
    businessName: "",
    businessType: "",
    businessLocation: {
      address: "",
      city: "",
      country: "",
    },
    contactPerson: {
      name: "",
      phone: "",
      email: "",
    },
    numberOfTills: 1,
    hardwareRequirements: {
      hasComputers: false,
      hasPrinters: false,
      hasScanners: false,
      hasBarcodeScanners: false,
      hasCashDrawers: false,
      otherHardware: [],
    },
    softwareRequirements: {
      inventoryManagement: true,
      customerManagement: true,
      reporting: true,
      multiBranch: false,
      integrations: [],
    },
    estimatedUsers: 1,
    budgetRange: "standard",
  });
  const [authForm, setAuthForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await installationAPI.getMyRequests();

      setRequests(response.data);
      console.log(response.data);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: "warning", label: "Pending Review" },
      reviewed: { variant: "info", label: "Under Review" },
      site_visit_scheduled: {
        variant: "primary",
        label: "Site Visit Scheduled",
      },
      quote_sent: { variant: "success", label: "Quote Sent" },
      approved: { variant: "success", label: "Approved" },
      installation_scheduled: {
        variant: "primary",
        label: "Installation Scheduled",
      },
      in_progress: { variant: "info", label: "Installation in Progress" },
      completed: { variant: "success", label: "Completed" },
      cancelled: { variant: "danger", label: "Cancelled" },
    };

    const config = statusConfig[status] || {
      variant: "secondary",
      label: status,
    };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const calculateProgress = (request) => {
    const completedStages = request.timeline.filter(
      (stage) => stage.completed,
    ).length;
    const totalStages = request.timeline.length;
    return Math.round((completedStages / totalStages) * 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not scheduled";
    return new Date(dateString).toLocaleDateString();
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      // let user;
      // if (isLogin) {
      //   const loginRes = await authAPI.login(authForm.email, authForm.password);
      //   user = loginRes.data;
      // } else {
      //   const registerRes = await authAPI.register({
      //     username: authForm.username,
      //     email: authForm.email,
      //     password: authForm.password,
      //     role: "viewer",
      //   });
      //   user = registerRes.data;
      // }

      // localStorage.setItem("token", user.token);
      // localStorage.setItem("user", JSON.stringify(user));

      const requestData = {
        ...requestForm,
        contactPerson: {
          ...requestForm.contactPerson,
          email: authForm.email,
          name: authForm.username,
        },
      };

      await installationAPI.create(requestData);

      setMessage({ type: "success", text: "Request submitted successfully!" });
      setShowRequestModal(false);
      navigate("/");
    } catch (error) {
      setMessage({
        type: "danger",
        text: error.response?.data?.message || "Failed to submit request",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <Alert variant="info">Loading your requests...</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>My POS Installation Requests</h2>

        <Button variant="primary" onClick={() => setShowRequestModal(true)}>
          + New Request
        </Button>
      </div>

      {requests.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <h4 className="text-muted mb-4">No installation requests found</h4>
            <p className="mb-4">Start your POS installation journey today!</p>

            <Button
              variant="primary"
              size="lg"
              onClick={() => setShowRequestModal(true)}
            >
              Request Installation
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {requests.map((request) => (
            <Col md={6} lg={4} key={request._id} className="mb-4">
              <Card className="h-100">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <strong>{request.businessName}</strong>
                  {getStatusBadge(request.status)}
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <small className="text-muted d-block">Business Type</small>
                    <strong>{request.businessType}</strong>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted d-block">
                      Tills Requested
                    </small>
                    <strong>{request.numberOfTills} terminal(s)</strong>
                  </div>

                  <div className="mb-4">
                    <small className="text-muted d-block">Progress</small>
                    <ProgressBar
                      now={calculateProgress(request)}
                      label={`${calculateProgress(request)}%`}
                      className="mb-2"
                    />
                    <small className="text-muted">
                      {request.timeline.filter((s) => s.completed).length} of{" "}
                      {request.timeline.length} stages completed
                    </small>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Requested: {formatDate(request.createdAt)}
                    </small>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        setSelectedRequest(request);
                        setShowDetailsModal(true);
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal
        show={showRequestModal}
        onHide={() => setShowRequestModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Request POS Installation</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleRequestSubmit}>
          <Modal.Body>
            {message.text && (
              <div className={`alert alert-${message.type}`}>
                {message.text}
              </div>
            )}

            <Tab.Container defaultActiveKey="business">
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="business">Business Details</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="hardware">Hardware</Nav.Link>
                </Nav.Item>
                {/* <Nav.Item>
                        <Nav.Link eventKey="software">Software</Nav.Link>
                      </Nav.Item> */}
              </Nav>

              <Tab.Content>
                <Tab.Pane eventKey="business">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Business Name *</Form.Label>
                        <Form.Control
                          type="text"
                          value={requestForm.businessName}
                          onChange={(e) =>
                            setRequestForm({
                              ...requestForm,
                              businessName: e.target.value,
                            })
                          }
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Business Type *</Form.Label>
                        <Form.Select
                          value={requestForm.businessType}
                          onChange={(e) =>
                            setRequestForm({
                              ...requestForm,
                              businessType: e.target.value,
                            })
                          }
                          required
                        >
                          <option value="">Select Type</option>
                          <option value="retail">Retail Store</option>
                          <option value="supermarket">Supermarket</option>
                          <option value="restaurant">Restaurant</option>
                          <option value="hotel">Hotel</option>
                          <option value="pharmacy">Pharmacy</option>
                          <option value="other">Other</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Business Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={requestForm.businessLocation.address}
                      onChange={(e) =>
                        setRequestForm({
                          ...requestForm,
                          businessLocation: {
                            ...requestForm.businessLocation,
                            address: e.target.value,
                          },
                        })
                      }
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Contact *</Form.Label>
                    <Form.Control
                      type="text"
                      value={requestForm.contactPerson.phone}
                      onChange={(e) =>
                        setRequestForm({
                          ...requestForm,
                          contactPerson: {
                            ...requestForm.contactPerson,
                            phone: e.target.value,
                          },
                        })
                      }
                      required
                    />
                  </Form.Group>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Number of Tills *</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={requestForm.numberOfTills}
                          onChange={(e) =>
                            setRequestForm({
                              ...requestForm,
                              numberOfTills: parseInt(e.target.value),
                            })
                          }
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Estimated Users *</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={requestForm.estimatedUsers}
                          onChange={(e) =>
                            setRequestForm({
                              ...requestForm,
                              estimatedUsers: parseInt(e.target.value),
                            })
                          }
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Tab.Pane>

                <Tab.Pane eventKey="hardware">
                  <h6 className="mb-3">What hardware do you already have?</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Check
                        type="checkbox"
                        label="Computers/Laptops"
                        checked={requestForm.hardwareRequirements.hasComputers}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            hardwareRequirements: {
                              ...requestForm.hardwareRequirements,
                              hasComputers: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Receipt Printers"
                        checked={requestForm.hardwareRequirements.hasPrinters}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            hardwareRequirements: {
                              ...requestForm.hardwareRequirements,
                              hasPrinters: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Barcode Scanners"
                        checked={
                          requestForm.hardwareRequirements.hasBarcodeScanners
                        }
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            hardwareRequirements: {
                              ...requestForm.hardwareRequirements,
                              hasBarcodeScanners: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Check
                        type="checkbox"
                        label="Cash Drawers"
                        checked={
                          requestForm.hardwareRequirements.hasCashDrawers
                        }
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            hardwareRequirements: {
                              ...requestForm.hardwareRequirements,
                              hasCashDrawers: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Document Scanners"
                        checked={requestForm.hardwareRequirements.hasScanners}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            hardwareRequirements: {
                              ...requestForm.hardwareRequirements,
                              hasScanners: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Other Hardware (comma separated)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., Customer display, Card reader, etc."
                      onChange={(e) =>
                        setRequestForm({
                          ...requestForm,
                          hardwareRequirements: {
                            ...requestForm.hardwareRequirements,
                            otherHardware: e.target.value
                              .split(",")
                              .map((item) => item.trim()),
                          },
                        })
                      }
                    />
                  </Form.Group>
                </Tab.Pane>

                <Tab.Pane eventKey="software">
                  <h6 className="mb-3">Required Features</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Check
                        type="checkbox"
                        label="Inventory Management"
                        checked={
                          requestForm.softwareRequirements.inventoryManagement
                        }
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            softwareRequirements: {
                              ...requestForm.softwareRequirements,
                              inventoryManagement: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Customer Management"
                        checked={
                          requestForm.softwareRequirements.customerManagement
                        }
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            softwareRequirements: {
                              ...requestForm.softwareRequirements,
                              customerManagement: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Multi-Branch Support"
                        checked={requestForm.softwareRequirements.multiBranch}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            softwareRequirements: {
                              ...requestForm.softwareRequirements,
                              multiBranch: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Check
                        type="checkbox"
                        label="Advanced Reporting"
                        checked={requestForm.softwareRequirements.reporting}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            softwareRequirements: {
                              ...requestForm.softwareRequirements,
                              reporting: e.target.checked,
                            },
                          })
                        }
                        className="mb-2"
                      />
                    </Col>
                  </Row>

                  {/* <Form.Group className="mb-3">
                          <Form.Label>Integrations Needed</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., Accounting software, Mobile banking, etc."
                            onChange={(e) =>
                              setRequestForm({
                                ...requestForm,
                                softwareRequirements: {
                                  ...requestForm.softwareRequirements,
                                  integrations: e.target.value
                                    .split(",")
                                    .map((item) => item.trim()),
                                },
                              })
                            }
                          />
                        </Form.Group> */}

                  {/* <Form.Group className="mb-3"> 
                          <Form.Label>Budget Range</Form.Label>
                          <Form.Select
                            value={requestForm.budgetRange}
                            onChange={(e) =>
                              setRequestForm({
                                ...requestForm,
                                budgetRange: e.target.value,
                              })
                            }
                          >
                            <option value="basic">Basic (KES 10,000 - 20,000)</option>
                            <option value="standard">
                              Standard (KES 20,000 - 40,000)
                            </option>
                            <option value="premium">
                              Premium (KES 40,000 - 80,000)
                            </option>
                            <option value="enterprise">
                              Enterprise (KES 80,000+)
                            </option>
                          </Form.Select>
                        </Form.Group>*/}
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowRequestModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="pos-primary"
              type="submit"
              disabled={loading}
              style={{ backgroundColor: POS_COLOR, borderColor: POS_COLOR }}
            >
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default CustomerDashboard;
